import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RichView from "../../components/design/RichView";
import { GOAL_DONE_RULE } from "../Schedule/rdSummary";
import { Banner, DataRow, Disclosure, EmptyState, StatusChip } from "../../components/mui";
import { MdAdd, MdCheck, MdCircle, MdErrorOutline, MdInfoOutline, MdRemove, MdWarningAmber } from "react-icons/md";

/**
 * 專案詳情 › 里程碑／成員／文件／會議紀錄四顆分頁的內容
 * （設計稿 #screen-project-data，el-pm-template_4.html L1286-L1394）
 *
 * 設計稿把這四區收在同一顆分頁裡、各自一個 <details>，理由是四種東西加起來一個專案
 * 也才二三十筆，分成四格只是逼人一格一格點過去找。上線後使用者反映那一顆分頁的名字
 * 是個籠統的總稱，會誤導：看不出成員、會議紀錄在裡面，找到了還要再點一次展開。
 * 所以四區升成 Detail.js 的四顆同級分頁，這裡依 section 只畫其中一區。
 *
 * **不再包一層收合**：升上來就是為了一點分頁就看到內容，再包一層收合等於
 * 把那一次多餘的點擊原封不動搬回來。原本掛在 summary 上的筆數改掛在分頁上（Detail.js），
 * 「收著也知道裡面有沒有東西」的作用由分頁接手。
 * 會議紀錄**每一筆**自己的收合區留著 —— 那是同一區裡十幾筆要不要一口氣攤開的問題，
 * 跟這次拆掉的那一層無關（見下面會議紀錄那段的說明）。
 *
 * 版面規則：
 *   · 空的區塊整塊換成空狀態（硬規則 2），不要畫一張空卡片。
 *   · 只有「新增第一筆會議紀錄」是 contained 主要按鈕（照抄設計稿 L1388）；其餘新增入口
 *     維持空狀態的 outlined 按鈕與清單底下 text 按鈕的文字連結。
 *     現在一次只畫一區，四區不會同時各冒一顆主鈕，硬規則 1 已經不是限制；
 *     但設計稿只給會議紀錄那一顆，其餘三區這次只是換容器、不是重新設計，所以沒有跟著升級。
 *   · 列不是整列可點的按鈕（RowButton）而是一般的 DataRow：里程碑／文件／成員／會議每一列都有
 *     兩三個動作（編輯、刪除、下載），巢狀 <button> 是無效的 HTML，點了也分不出
 *     使用者要的是哪一個。動作各自是帶文字、吃得到 44px 的 text 按鈕（硬規則 3）。
 */

/** 四區的鍵，跟 Detail.js 的分頁鍵是同一組字串（那裡直接把分頁鍵當 section 傳進來） */
const SECTIONS = ["phases", "members", "docs", "meetings"];

/** 里程碑狀態 → StatusChip。文字用里程碑自己的語彙（規格書寫「未開始／進行中／已完成」），
 *  不要印出工項的「待處理」—— 同一個系統裡講兩套話，使用者會以為是兩種東西 */
const PHASE_TAG = {
  pending: { kind: "closed", mark: <MdRemove />, text: "未開始" },
  progress: { kind: "progress", mark: <MdCircle size="0.6em" />, text: "進行中" },
  done: { kind: "done", mark: <MdCheck />, text: "已完成 · 工項全數完成" },
};

/** 今天距里程碑日期過了幾天（沒過、或沒訂日期都是 0）。用當地日界切日，跟時程頁的 ymdOf 一致 */
function daysPast(endRaw) {
  if (!endRaw) return 0;
  const end = dayjs(endRaw);
  if (!end.isValid()) return 0;
  const diff = dayjs().startOf("day").diff(end.startOf("day"), "day");
  return diff > 0 ? diff : 0;
}

/**
 * 里程碑那一列的狀態徽章，跟時程頁（pages/Schedule/Goals.js 的 goalTag）同一套說法：
 *   · 沒有工項：判定不了完成也判定不了逾期 →「未開始 · 尚無工項」，過了日期也只講事實，灰色。
 *   · 工項全部完成：後端回 status = 2 →「已完成 · 工項全數完成」。
 *   · 有工項、還沒做完、日期已過 → 紅色「已過里程碑日期 N 天 · 尚未完成」；否則照後端的未開始／進行中。
 * 狀態值本身由後端依工項推導（PutPhase 不收狀態），這裡只決定怎麼講。
 */
function phaseTag(p) {
  const past = daysPast(p.endRaw);
  if (!(p.items > 0)) {
    return {
      kind: "closed",
      mark: <MdRemove />,
      text: past > 0 ? `已過里程碑日期 ${past} 天 · 尚無工項` : "未開始 · 尚無工項",
    };
  }
  if (p.statusKey === "done") return PHASE_TAG.done;
  if (past > 0) return { kind: "attention", mark: <MdWarningAmber />, text: `已過里程碑日期 ${past} 天 · 尚未完成` };
  return PHASE_TAG[p.statusKey] || PHASE_TAG.pending;
}

/** 一個人負責的比例超過這個數，就算「工作量不平均」。低於三件的專案不判斷 —— 兩件全給同一個人很正常 */
const HEAVY_SHARE = 0.6;
const HEAVY_MIN = 3;

/** 區塊底部的「＋ 加入…」文字入口（硬規則 2：空的區塊只留這個入口，不要畫空卡片） */
function AddLink({ text, onClick }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Button variant="text" type="button" startIcon={<MdAdd aria-hidden="true" />} onClick={onClick}>
        {text}
      </Button>
    </Box>
  );
}

AddLink.propTypes = { text: PropTypes.string.isRequired, onClick: PropTypes.func };
AddLink.defaultProps = { onClick: () => {} };

function DetailData(props) {
  const {
    section,
    phases,
    docs,
    members,
    meetings,
    projectPct,
    canCreatePhase,
    canEditPhase,
    canSeeTask,
    canCreateMeeting,
    canEditProject,
    onAddPhase,
    onEditPhase,
    onDeletePhase,
    onAddDoc,
    onDownloadDoc,
    onDeleteDoc,
    onAddMember,
    onRemoveMember,
    onAddMeeting,
    onEditMeeting,
    onDeleteMeeting,
    onDownloadMeetingFile,
  } = props;

  /* 文件的「只看」分類（設計稿 L1325-L1328）。"all" = 全部 */
  const [docCat, setDocCat] = useState("all");

  /* ---------- 里程碑的數字對不對得上 ---------- */

  /*
   * 「里程碑都 0%、專案卻有進度」要主動講出來（設計稿 L1298-L1301）。
   *
   * 這個系統裡里程碑的進度是後端依底下的工項算出來的（完成數 ÷ 工項數），
   * 所以全部 0% 幾乎都只有兩種原因：工項沒有掛進里程碑，或掛進來的都還沒完成。
   * 不講的話，使用者會以為是自己看錯，或以為系統把進度算丟了。
   */
  const phaseWarn = useMemo(() => {
    if (phases.length === 0) return null;
    if (!(projectPct > 0)) return null;
    if (phases.some((p) => p.pct > 0)) return null;
    const noItems = phases.every((p) => p.items === 0);
    return {
      /* 跟 Detail.js 摘要卡上那一條同一個標題：同一件事在兩個分頁講成兩種說法，會被當成兩件事 */
      title: "里程碑進度與專案進度不一致",
      text: noItems
        ? `${phases.length} 個里程碑下皆尚無工項（因此皆顯示 0%），但專案整體進度已達 ${projectPct}%。里程碑進度依其下工項計算，將工項歸入里程碑後才會更新。`
        : `${phases.length} 個里程碑的進度皆為 0%，但專案整體進度已達 ${projectPct}%。里程碑進度依其下工項計算，目前歸入的工項皆尚未完成。`,
    };
  }, [phases, projectPct]);

  /** 重名的里程碑：工項掛進去之後分不出是哪一個，名字重複一定要講（設計稿 L1300 的後半句） */
  const dupNames = useMemo(() => {
    const seen = new Map();
    phases.forEach((p) => {
      const n = String(p.name || "").trim();
      if (!n) return;
      seen.set(n, (seen.get(n) || 0) + 1);
    });
    return [...seen.entries()].filter(([, n]) => n > 1).map(([name]) => name);
  }, [phases]);

  /* ---------- 成員的工作量 ---------- */

  const workload = useMemo(() => {
    if (members.length < 2) return null;
    const total = members.reduce((s, m) => s + (Number(m.tasks) || 0), 0);
    if (total < HEAVY_MIN) return null;
    const top = members.reduce((a, b) => ((Number(b.tasks) || 0) > (Number(a.tasks) || 0) ? b : a));
    const topTasks = Number(top.tasks) || 0;
    if (topTasks / total < HEAVY_SHARE) return null;
    return { name: top.name, id: top.id, tasks: topTasks, others: members.length - 1, rest: total - topTasks };
  }, [members]);

  /* ---------- 文件分類 ---------- */

  const docCats = useMemo(() => {
    const counts = new Map();
    docs.forEach((d) => counts.set(d.cat, (counts.get(d.cat) || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [docs]);

  const docsShown = useMemo(() => (docCat === "all" ? docs : docs.filter((d) => d.cat === docCat)), [docs, docCat]);

  return (
    <Box sx={{ mt: 3 }}>
      {/* ================= 里程碑 ================= */}
      {section === "phases" && (
        <>
          {phaseWarn && (
            <Banner kind="info" icon={<MdInfoOutline />}>
              <strong>{phaseWarn.title}　</strong>
              {phaseWarn.text}
            </Banner>
          )}
          {dupNames.length > 0 && (
            <Banner kind="warn" icon={<MdErrorOutline />} sx={{ mt: phaseWarn ? 1.5 : 0 }}>
              <strong>里程碑名稱重複　</strong>
              {dupNames.map((n) => `「${n}」`).join("、")}
              各有多個。將工項歸入里程碑時將無法區分，建議先變更名稱。
            </Banner>
          )}

          {phases.length === 0 ? (
            <EmptyState
              title="此專案尚無里程碑"
              text="將專案劃分為數個階段（需求訪談、開發、上線…），即可於時程頁檢視各階段進度。"
              action={
                canCreatePhase && (
                  <Button variant="outlined" type="button" onClick={onAddPhase}>
                    新增里程碑
                  </Button>
                )
              }
            />
          ) : (
            <>
              {/* 完成的定義：與時程頁的里程碑分頁同一句（rdSummary 的 GOAL_DONE_RULE） */}
              <Typography variant="body2" color="text.secondary" sx={{ mt: phaseWarn || dupNames.length ? 2 : 0 }}>
                {GOAL_DONE_RULE}
              </Typography>
              <Card sx={{ mt: 1 }}>
                {phases.map((p) => {
                  const tag = phaseTag(p);
                  return (
                    <DataRow
                      key={p.id}
                      title={p.name || "（未命名）"}
                      meta={
                        <>
                          {/* 取不到就照實說「還沒有」，不要填 0 或「—」充版面（硬規則 10） */}
                          <span>{p.items > 0 ? `${p.items} 件工項，已完成 ${p.doneItems} 件（${p.pct}%）` : "尚無工項"}</span>
                          <span>{p.start || p.end ? `${p.start || "？"} – ${p.end || "？"}` : "尚未排定時程"}</span>
                        </>
                      }
                      side={
                        <>
                          <StatusChip kind={tag.kind} mark={tag.mark}>
                            {tag.text}
                          </StatusChip>
                          {canEditPhase && (
                            <>
                              <Button variant="text" type="button" onClick={() => onEditPhase(p)}>
                                編輯
                              </Button>
                              {/*
                                底下還有工項就不給刪 —— DB 的 fk_task_phase 是 ON DELETE SET NULL，
                                硬刪其實刪得掉，但那一整批工項會安靜地變成「還沒排進里程碑」，沒有人會發現。
                                後端沒有這道檢查（DeletePhase 只刪），所以這一道一定要留在前端。
                                按鈕本身不隱藏也不 disable：點下去會用一句話講清楚現在為什麼不能刪。
                              */}
                              <Button variant="text" type="button" onClick={() => onDeletePhase(p)}>
                                刪除
                              </Button>
                            </>
                          )}
                        </>
                      }
                    />
                  );
                })}
              </Card>
              {canCreatePhase && <AddLink text="新增里程碑" onClick={onAddPhase} />}
            </>
          )}
        </>
      )}

      {/* ================= 文件 ================= */}
      {/* 沒有工項權限就整塊不畫：/Document/List 掛的是 task 權限，Detail.js 根本沒去問，
          留一個永遠 0 份的區塊比直接不顯示更容易被當成檔案掉了。
          Detail.js 在這種情況下連「文件」分頁都不放、?tab=docs 也會落到里程碑，
          這裡再擋一次是怕別處把 section 傳成 docs 時冒出那個假的空狀態 */}
      {section === "docs" && canSeeTask && (
        <>
          {docs.length === 0 ? (
            <EmptyState
              title="此專案尚無文件"
              text="需求書、設計稿、合約等文件可集中存放於此，避免分散於個人電腦。"
              action={
                <Button variant="outlined" type="button" onClick={onAddDoc}>
                  上傳文件
                </Button>
              }
            />
          ) : (
            <>
              {/* 只看（設計稿 L1325-L1328）。分類選項由這個專案真的有的文件推導，沒有那一類就不會出現 */}
              {docCats.length > 1 && (
                <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                  <Typography component="label" variant="body2" color="text.secondary" htmlFor="pd-doccat">
                    分類：
                  </Typography>
                  <TextField
                    select
                    id="pd-doccat"
                    value={docCat}
                    onChange={(e) => setDocCat(e.target.value)}
                    slotProps={{ select: { native: true } }}
                  >
                    <option value="all">全部 {docs.length}</option>
                    {docCats.map(([cat, n]) => (
                      <option key={cat} value={cat}>
                        {cat} {n}
                      </option>
                    ))}
                  </TextField>
                </Stack>
              )}

              {docsShown.length === 0 ? (
                <EmptyState
                  title={`「${docCat}」分類目前沒有文件`}
                  text="將上方「分類」改為「全部」即可檢視此專案的所有文件。"
                />
              ) : (
                <Card>
                  {docsShown.map((d) => (
                    <DataRow
                      key={d.id}
                      title={d.name || "（未命名檔案）"}
                      meta={
                        <>
                          <span>{d.cat}</span>
                          <span>{d.size}</span>
                          {d.uploaded ? <span>{d.uploaded} 上傳</span> : null}
                          {/* 說明後端一直存得下也回得出來，舊清單只放在 title 裡（等於填了沒人看得到） */}
                          {d.noteText ? <span>{d.noteText}</span> : null}
                        </>
                      }
                      side={
                        <>
                          <Button variant="text" type="button" onClick={() => onDownloadDoc(d)}>
                            下載
                          </Button>
                          <Button variant="text" type="button" onClick={() => onDeleteDoc(d)}>
                            刪除
                          </Button>
                        </>
                      }
                    />
                  ))}
                </Card>
              )}
              <AddLink text="上傳文件" onClick={onAddDoc} />
            </>
          )}
        </>
      )}

      {/* ================= 成員 ================= */}
      {section === "members" && (
        <>
          {workload && (
            <Banner kind="warn" icon={<MdErrorOutline />}>
              <strong>工作量分配不均　</strong>
              {`${workload.name} 獨自負責 ${workload.tasks} 件，其他 ${workload.others} 位合計 ${workload.rest} 件。`}
            </Banner>
          )}

          {members.length === 0 ? (
            <EmptyState
              title="此專案尚無成員"
              text="資料範圍設為「所屬專案」的角色，未加入此專案者將無法檢視專案內容。"
              action={
                canEditProject && (
                  <Button variant="outlined" type="button" onClick={onAddMember}>
                    新增成員
                  </Button>
                )
              }
            />
          ) : (
            <>
              <Card sx={{ mt: workload ? 2 : 0 }}>
                {members.map((m) => (
                  <DataRow
                    key={m.id}
                    title={m.name || m.account}
                    meta={
                      <>
                        <span>{m.role}</span>
                        <span>{m.tasks > 0 ? `負責 ${m.tasks} 件` : "尚無工項"}</span>
                        {m.email && m.email !== "—" ? <span>{m.email}</span> : null}
                      </>
                    }
                    side={
                      (workload && workload.id === m.id) || canEditProject ? (
                        <>
                          {workload && workload.id === m.id ? (
                            <StatusChip kind="attention" mark={<MdWarningAmber />}>
                              工作量過重
                            </StatusChip>
                          ) : null}
                          {canEditProject && (
                            <Button variant="text" type="button" onClick={() => onRemoveMember(m)}>
                              移出專案
                            </Button>
                          )}
                        </>
                      ) : null
                    }
                  />
                ))}
              </Card>
              {canEditProject && <AddLink text="新增成員" onClick={onAddMember} />}
            </>
          )}
        </>
      )}

      {/* ================= 會議紀錄 ================= */}
      {section === "meetings" && (
        <>
          {meetings.length === 0 ? (
            /* 照抄設計稿 L1384-L1390：這是這個分頁唯一的主要按鈕 */
            <EmptyState
              title="此專案尚無會議紀錄"
              text="會議決議可記錄於此，方便成員日後查閱。"
              action={
                canCreateMeeting && (
                  <Button variant="contained" type="button" onClick={onAddMeeting}>
                    新增會議紀錄
                  </Button>
                )
              }
            />
          ) : (
            <>
              <Stack spacing={1}>
                {meetings.map((m) => (
                  /*
                   * 一筆會議紀錄自己是一個收合區：決議事項、會議記錄（可能含截圖）、
                   * 附件都還在，只是不再一口氣把十幾筆全部攤開 ——
                   * 舊版一個專案的會議分頁要捲五六個畫面才看得完。
                   */
                  <Disclosure key={m.id} title={m.title || "（未填主題）"} count={`（${m.when || "未填日期"}）`}>
                    <Stack spacing={2}>
                      {m.location ? <Typography>地點：{m.location}</Typography> : null}
                      {m.attendees.length > 0 ? (
                        <Typography>出席：{m.attendees.map((a) => a.name).join("、")}</Typography>
                      ) : null}

                      {m.decisions ? (
                        <div>
                          <Typography sx={{ fontWeight: 600 }}>決議事項</Typography>
                          {/* 決議是純文字，換行要留著 —— 一條一條寫的決議擠成一段就看不出有幾條 */}
                          <Typography sx={{ whiteSpace: "pre-wrap" }}>{m.decisions}</Typography>
                        </div>
                      ) : null}

                      {/* 會議記錄是編輯器產生的 HTML（多半含截圖），照舊用 RichView 呈現 ——
                          圖片仍然點得開放大，排版也還在。字級走 --t-body，大字模式跟著放大 */}
                      {m.hasNotes ? (
                        <RichView
                          html={m.notes}
                          sxStyle="font-size:var(--t-body);line-height:var(--t-body-lh);color:var(--ink);overflow-x:auto;"
                        />
                      ) : null}

                      {!m.decisions && !m.hasNotes && !m.hasFile ? (
                        <Typography>此筆紀錄尚未填寫內容，也尚未上傳檔案。</Typography>
                      ) : null}

                      {/* 一個動作都沒有就整排不畫（硬規則 2），不要留一條空著的動作列 */}
                      {(m.hasFile || canEditProject) && (
                        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                          {m.hasFile && (
                            <Button variant="outlined" type="button" onClick={() => onDownloadMeetingFile(m)}>
                              下載 {m.fileName || "會議記錄檔"}
                            </Button>
                          )}
                          {canEditProject && (
                            <>
                              <Button variant="text" type="button" onClick={() => onEditMeeting(m)}>
                                編輯
                              </Button>
                              <Button variant="text" type="button" onClick={() => onDeleteMeeting(m)}>
                                刪除
                              </Button>
                            </>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Disclosure>
                ))}
              </Stack>
              {canCreateMeeting && <AddLink text="新增會議紀錄" onClick={onAddMeeting} />}
            </>
          )}
        </>
      )}
    </Box>
  );
}

DetailData.propTypes = {
  /** 要畫哪一區；就是 Detail.js 目前選中的分頁鍵 */
  section: PropTypes.oneOf(SECTIONS),
  /** [{ id, name, start, end, endRaw, statusKey, pct, items, doneItems }] */
  phases: PropTypes.array,
  /** [{ id, name, cat, size, who, when, noteText }] */
  docs: PropTypes.array,
  /** [{ id, name, account, role, email, tasks }] */
  members: PropTypes.array,
  /** [{ id, title, when, location, attendees, decisions, notes, hasNotes, hasFile, fileName }] */
  meetings: PropTypes.array,
  /** 專案整體進度（後端算的），用來對比里程碑是不是全部 0% */
  projectPct: PropTypes.number,
  canCreatePhase: PropTypes.bool,
  canEditPhase: PropTypes.bool,
  canSeeTask: PropTypes.bool,
  canCreateMeeting: PropTypes.bool,
  canEditProject: PropTypes.bool,
  onAddPhase: PropTypes.func,
  onEditPhase: PropTypes.func,
  onDeletePhase: PropTypes.func,
  onAddDoc: PropTypes.func,
  onDownloadDoc: PropTypes.func,
  onDeleteDoc: PropTypes.func,
  onAddMember: PropTypes.func,
  onRemoveMember: PropTypes.func,
  onAddMeeting: PropTypes.func,
  onEditMeeting: PropTypes.func,
  onDeleteMeeting: PropTypes.func,
  onDownloadMeetingFile: PropTypes.func,
};

DetailData.defaultProps = {
  /* 預設里程碑：跟 Detail.js 的落點規則一致（沒有工項權限、或舊的 ?tab=data 都落在這裡） */
  section: "phases",
  phases: [],
  docs: [],
  members: [],
  meetings: [],
  projectPct: 0,
  canCreatePhase: false,
  canEditPhase: false,
  canSeeTask: false,
  canCreateMeeting: false,
  canEditProject: false,
  onAddPhase: () => {},
  onEditPhase: () => {},
  onDeletePhase: () => {},
  onAddDoc: () => {},
  onDownloadDoc: () => {},
  onDeleteDoc: () => {},
  onAddMember: () => {},
  onRemoveMember: () => {},
  onAddMeeting: () => {},
  onEditMeeting: () => {},
  onDeleteMeeting: () => {},
  onDownloadMeetingFile: () => {},
};

export default DetailData;
